/**
 * Cliente de la agenda unificada del mes. **Una llamada, y es GET.**
 *
 * La unica puerta a la red de este archivo es `api.fetchReadOnly`, que no acepta
 * metodo ni cuerpo: un calendario se lee, y desde aca no hay forma de escribir
 * nada aunque alguien quisiera. Mismo criterio —y mismo motivo— que el cliente
 * de la busqueda general (`views/busqueda/api.ts`) y el del chat de ayuda. Del
 * otro lado, la vista del backend declara `get()` y ningun otro verbo, asi que
 * cualquier intento contesta 405.
 *
 * NADA DEL VOCABULARIO DEL OFICIO ESTA ESCRITO ACA. Que existan las ventas, los
 * reclamos y los pendientes lo dice el servidor, en `tipo` y `tipo_label`; que
 * una venta renovada este fuera de juego lo dice `apagado`. El calendario dibuja
 * lo que le mandan. Lo unico que este lado decide es el COLOR de cada tipo, que
 * no es un dato que un backend pueda dar razonablemente — y aun asi hay un color
 * por defecto para el tipo que no conozca, asi que una cuarta fuente aparece
 * sola en la pantalla en vez de romperla.
 *
 * LA RUTA NO ESTA ESCRITA ACA TAMPOCO: la declara el modulo dueño del endpoint,
 * en el `context` de su accion de menu (`calendar_agenda_url`). Por eso este
 * archivo no menciona la palabra «consorcios» en ningun lado: sin ese dato el
 * calendario cae a su modo de una sola fuente, que es lo que era antes.
 */

import { api } from '@/lib/api';

/**
 * Una cosa que vence, en la forma comun a las tres fuentes.
 *
 * Espeja `ItemDeAgenda` de `property_mgmt/services/agenda.py`. Si cambia una,
 * cambia la otra.
 */
export interface ItemDeAgenda {
  /** `venta` | `reclamo` | `pendiente`. Es lo que da el color y el icono. */
  tipo: string;
  /** Como se llama ese tipo en la calle. Viene del servidor: no se traduce aca. */
  tipo_label: string;
  /**
   * Clave unica DEL ITEM, no del registro al que enlaza.
   *
   * Hace falta que sea propia por un caso real: un pendiente enlaza al registro
   * al que esta pegado, asi que un pendiente de la venta 7 y la venta 7 misma
   * comparten `(modelo, id)` y se pisarian como clave de lista de React.
   */
  clave: string;
  /** `YYYY-MM-DD`. Siempre presente. */
  fecha: string;
  titulo: string;
  detalle: string;
  /** Texto, nunca numero: los importes viajan como string (regla 4). `null` si la fuente no tiene plata. */
  importe: string | null;
  /** Adonde lleva el clic. */
  modelo: string;
  id: number;
  estado: string;
  estado_label: string;
  /** Ya salio de juego (venta renovada o anulada, reclamo cerrado, pendiente hecho). */
  apagado: boolean;
}

export interface TipoDeAgenda {
  valor: string;
  etiqueta: string;
}

export interface RespuestaDeAgenda {
  desde: string;
  hasta: string;
  items: ItemDeAgenda[];
  /** Por fuente: `true` si se alcanzo el tope y falta algo. */
  truncado: Record<string, boolean>;
  /**
   * Fuentes que el usuario NO puede leer.
   *
   * Es la diferencia entre «este mes no vence nada» y «vos no ves las ventas»,
   * y confundirlas hace que alguien concluya que el dato no esta cargado.
   */
  sin_permiso: string[];
  tipos: TipoDeAgenda[];
  tope_por_fuente: number;
}

/**
 * Todo lo que vence entre dos fechas, de las tres fuentes a la vez.
 *
 * @param ruta la que declaro el modulo en `context.calendar_agenda_url`.
 */
export function agendaDelRango(
  ruta: string,
  desde: string,
  hasta: string,
  signal?: AbortSignal,
): Promise<RespuestaDeAgenda> {
  return api.fetchReadOnly<RespuestaDeAgenda>(
    ruta.startsWith('/') ? ruta : `/${ruta}`,
    { desde, hasta },
    signal,
  );
}

/**
 * ¿Esta respuesta es una agenda de verdad?
 *
 * No es paranoia de tipos: el backend falso del archivo suelto contesta
 * `{detail, results: [], count: 0}` con estado 200 a toda ruta que no tenga
 * grabada, asi que «llego sin error» no alcanza. Con esto, una foto vieja —o un
 * despliegue sin el endpoint— cae al calendario de una sola fuente en vez de
 * mostrar para siempre un mes vacio, que es una mentira mas cara que una
 * pantalla con menos cosas.
 */
export function esAgendaValida(datos: unknown): datos is RespuestaDeAgenda {
  if (datos === null || typeof datos !== 'object') return false;
  const posible = datos as Partial<RespuestaDeAgenda>;
  return Array.isArray(posible.items) && Array.isArray(posible.tipos);
}
